/**
 * Support chat in the signed-in app (Anand, 13 Oct 26: "Support chat, add
 * widget.", WO-69).
 *
 * Crisp itself is loaded once for the whole site by CrispChatWrapper (same
 * website id as the marketing pages, after the page goes idle). What the app
 * adds, and what this module holds:
 *
 *   - who and what the person is looking at, for support: the workspace (or
 *     the whole account) and the location on screen. AppShell (the account
 *     pages) and AppLocationShell (a location's pages) set it while they are
 *     mounted; CrispChatWrapper reads it and sends it as Crisp session data
 *     with the account's email and name, so support knows who is talking;
 *   - html[data-fly-support-chat] while either shell is on screen, which the
 *     launcher's position rules key on (src/styles/agent-app-support-chat.css:
 *     bottom right, over the phone tab bar and the Ask the agent button,
 *     under every sheet and dialog);
 *   - while either shell is up, the launcher follows the app's rule for the
 *     screen width (src/lib/flyChat.ts tucksChatLauncher): tucked from a
 *     tablet up (Anand, 15 Oct 26: the support bubble stacked over the Ask
 *     the agent bubble on desktop), tucked on a location's pages on a phone
 *     (the tab bar's Chat fan), shown on the account pages on a phone;
 *   - openSupportChat, for Chat with us now on /app/support, and
 *     chatWithFlySupport, for "Chat with Fly support" in the sidebar, the
 *     rail and the Menu (falls back to the mail app when Crisp cannot open).
 *
 * Not on the shared links (/agent/...): they render neither shell, and the
 * launcher stays tucked there (src/lib/flyChat.ts tucksChatLauncher). Not on
 * a business's customer pages (/book, /booking, /f): CrispChatWrapper never
 * loads Crisp there.
 *
 * Every value sent is one the app already holds. When there is no single
 * workspace on screen the keys say so ("All workspaces", "all"), and with no
 * location on screen "none", so a value from an earlier page never stays in
 * the session as if it were current (Crisp keeps session data until it is
 * set again; there is no unset).
 */
import { useEffect } from 'react';
import { followFlyChatRule, openFlyChat } from './flyChat';

/** The attribute on <html> the launcher rules key on. */
export const APP_CHAT_ATTR = 'data-fly-support-chat';

/** The name sent for the account wide view, the words the workspace card uses for it. */
export const ALL_WORKSPACES = 'All workspaces';

export interface AppChatWorkspace {
  id: string;
  name: string;
}

export interface AppChatContext {
  /** The account's own name (profiles.full_name), when the app has it. */
  userName: string | null;
  /** The workspace on screen; 'all' for the whole account; null when it is not known. */
  workspace: AppChatWorkspace | 'all' | null;
  /** The location on screen, its name when known; null when the page is about no location. */
  location: { id: string; name: string | null } | null;
}

/** The Crisp session data keys this module writes. */
export const APP_CHAT_KEYS = ['workspace_name', 'workspace_id', 'location_name', 'location_id'] as const;

const clean = (v: string | null | undefined): string => (typeof v === 'string' ? v.trim() : '');

/** The session data for Crisp, in a fixed order. Pure. */
export function appChatSessionData(ctx: AppChatContext): Record<(typeof APP_CHAT_KEYS)[number], string> {
  const ws = ctx.workspace;
  const loc = ctx.location;
  return {
    workspace_name: ws === 'all' ? ALL_WORKSPACES : ws ? clean(ws.name) || 'unknown' : 'unknown',
    workspace_id: ws === 'all' ? 'all' : ws ? clean(ws.id) || 'unknown' : 'unknown',
    location_name: loc ? clean(loc.name) || 'unknown' : 'none',
    location_id: loc ? clean(loc.id) || 'unknown' : 'none',
  };
}

/**
 * The workspace the account pages show, the way the workspace card names it
 * (AppShell): the one in ?ws=, else the only one, else the whole account.
 * Pure.
 */
export function shownWorkspace(workspaces: ReadonlyArray<{ workspace_id: string; name: string }>, activeId: string | null): AppChatWorkspace | 'all' {
  const active = activeId ? workspaces.find((w) => w.workspace_id === activeId) : undefined;
  const one = active ?? (workspaces.length === 1 ? workspaces[0] : undefined);
  return one ? { id: one.workspace_id, name: one.name } : 'all';
}

/* ----------------------------------------------------------- the store */

let current: AppChatContext | null = null;
const listeners = new Set<() => void>();

const sameContext = (a: AppChatContext | null, b: AppChatContext | null): boolean => JSON.stringify(a) === JSON.stringify(b);

/** What the app shows now, or null when no app page is on screen. */
export function getAppChatContext(): AppChatContext | null {
  return current;
}

/** Set (or clear, with null) what the app shows. Listeners hear only a real change. */
export function setAppChatContext(next: AppChatContext | null): void {
  if (sameContext(current, next)) return;
  current = next;
  for (const fn of [...listeners]) fn();
}

/** Hear every change, for useSyncExternalStore. */
export function subscribeAppChatContext(fn: () => void): () => void {
  listeners.add(fn);
  return () => {
    listeners.delete(fn);
  };
}

/**
 * While the page is mounted: the context for support, and the attribute the
 * launcher's position keys on. Both go when it unmounts, so a shared link or
 * a marketing page after it shows neither.
 */
export function useAppSupportChat(ctx: AppChatContext): void {
  const key = JSON.stringify(ctx);
  useEffect(() => {
    const value = JSON.parse(key) as AppChatContext;
    setAppChatContext(value);
    console.log('[appSupportChat] support chat context', appChatSessionData(value));
    return () => setAppChatContext(null);
  }, [key]);
  useEffect(() => {
    if (typeof document === 'undefined') return undefined;
    document.documentElement.setAttribute(APP_CHAT_ATTR, '');
    return () => document.documentElement.removeAttribute(APP_CHAT_ATTR);
  }, []);
  // The launcher where the app's rule puts it for this width, and again when the width changes.
  useEffect(() => followFlyChatRule(), []);
}

/* ------------------------------------------------------ Chat with us now */

/** How long Chat with us now waits for Crisp to load before saying it did not open. */
export const OPEN_WAIT_MS = 8000;
const OPEN_POLL_MS = 250;

/**
 * True once Crisp's own client runs the page's $crisp (it brings get and is);
 * before that $crisp is the plain queue the loader set up, which a blocked
 * script never empties.
 */
function crispRunning(): boolean {
  const c = typeof window === 'undefined' ? null : (window as unknown as { $crisp?: { get?: unknown } }).$crisp;
  return !!c && typeof c.get === 'function';
}

/**
 * Opens the support chat. Crisp loads after the page goes idle, so a tap that
 * comes first waits for its client (up to OPEN_WAIT_MS) and opens it then.
 * False when it never ran (a blocker, or a page where Crisp does not load),
 * so the page can say so instead of a button that does nothing.
 */
export function openSupportChat(waitMs = OPEN_WAIT_MS): Promise<boolean> {
  if (crispRunning() && openFlyChat()) {
    console.log('[appSupportChat] chat opened');
    return Promise.resolve(true);
  }
  console.log('[appSupportChat] Crisp is not running on the page yet, waiting for it', { wait_ms: waitMs });
  return new Promise((resolve) => {
    const started = Date.now();
    const timer = setInterval(() => {
      if (crispRunning() && openFlyChat()) {
        clearInterval(timer);
        console.log('[appSupportChat] chat opened after waiting', { waited_ms: Date.now() - started });
        resolve(true);
        return;
      }
      if (Date.now() - started >= waitMs) {
        clearInterval(timer);
        console.warn('[appSupportChat] Crisp never ran here, the chat did not open', { waited_ms: Date.now() - started });
        resolve(false);
      }
    }, OPEN_POLL_MS);
  });
}

/* ------------------------------------------------- Chat with Fly support */

/** Fly support's address, for the mail app when the chat cannot open. */
export const SUPPORT_EMAIL = 'fly@zoomlocal.in';
export const SUPPORT_MAILTO = `mailto:${SUPPORT_EMAIL}`;

/**
 * How long Chat with Fly support waits for Crisp before the mail app opens
 * instead. Shorter than Chat with us now's wait: a browser may refuse to hand
 * a mailto: address to the mail app once the tap is several seconds old.
 */
export const NAV_OPEN_WAIT_MS = 4000;

/** Crisp's queue or its client is on the page (CrispChatWrapper puts it there; some pages and accounts never get it). */
function crispOnPage(): boolean {
  const c = typeof window === 'undefined' ? null : (window as unknown as { $crisp?: { push?: unknown } }).$crisp;
  return !!c && typeof c.push === 'function';
}

/**
 * "Chat with Fly support" in the sidebar, the rail and the Menu (Anand,
 * 15 Oct 26): opens Crisp's chat window, waiting for its client while it
 * loads (openSupportChat). When Crisp is not on the page, or never runs (a
 * blocker, offline), the mail app opens to fly@zoomlocal.in instead, so the
 * tap always reaches support. Resolves which of the two happened.
 */
export async function chatWithFlySupport(waitMs = NAV_OPEN_WAIT_MS): Promise<'chat' | 'mail'> {
  if (crispOnPage() && (await openSupportChat(waitMs))) return 'chat';
  console.warn('[appSupportChat] the support chat could not open here, opening the mail app instead', { to: SUPPORT_EMAIL });
  window.open(SUPPORT_MAILTO, '_self');
  return 'mail';
}
